import React from "react";
import SectionCrudPage from "./SectionCrudPage";
import {
  CrmDateTimeInput,
  CrmFormGrid,
  CrmNumberInput,
  CrmSelectInput,
  CrmTextInput,
} from "../../components/crm/CrudFormControls";

const asText = (value) => String(value ?? "").trim();
const asNullableText = (value) => {
  const next = asText(value);
  return next === "" ? null : next;
};
const asNullable = (value) => (value === "" || value === undefined ? null : value);
const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
const asNullableNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  return asNumber(value);
};
const asDateTime = (value) => {
  const next = asText(value);
  if (next === "") return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(next)) return `${next.replace("T", " ")}:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(next)) return next.replace("T", " ");
  return next;
};

const selectSource = (name, endpoint, labelFields, valueField = "id") => ({
  name,
  optionSource: { endpoint, valueField, labelFields },
});

function SalesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="user_id"
        label="Customer"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.user_id || []}
        nullable
      />
      <CrmSelectInput
        name="sold_by_user_id"
        label="Sold By"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.sold_by_user_id || []}
        nullable
      />
      <CrmSelectInput
        name="channel"
        label="Channel"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "pos", label: "POS" },
          { value: "online", label: "Online" },
          { value: "app", label: "App" },
        ]}
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "draft", label: "Draft" },
          { value: "paid", label: "Paid" },
          { value: "cancelled", label: "Cancelled" },
          { value: "refunded", label: "Refunded" },
        ]}
      />
      <CrmNumberInput
        name="subtotal"
        label="Subtotal"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="discount_total"
        label="Discount Total"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="tax_total"
        label="Tax Total"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="total"
        label="Total"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="currency"
        label="Currency"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="occurred_at"
        label="Occurred At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function SaleLinesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="sale_id"
        label="Sale"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.sale_id || []}
      />
      <CrmSelectInput
        name="product_id"
        label="Product"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.product_id || []}
      />
      <CrmNumberInput
        name="qty"
        label="Quantity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="unit_price"
        label="Unit Price"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="line_subtotal"
        label="Line Subtotal"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="vat_rate_id"
        label="VAT Rate"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.vat_rate_id || []}
        nullable
      />
      <CrmNumberInput
        name="vat_rate_snapshot"
        label="VAT Rate Snapshot"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="vat_amount"
        label="VAT Amount"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="product_name_snapshot"
        label="Product Name Snapshot"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="cost_snapshot"
        label="Cost Snapshot"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function SalePaymentsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="sale_id"
        label="Sale"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.sale_id || []}
      />
      <CrmSelectInput
        name="method"
        label="Method"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "cash", label: "Cash" },
          { value: "card", label: "Card" },
          { value: "mbway", label: "MBWay" },
          { value: "bank_transfer", label: "Bank Transfer" },
          { value: "direct_debit", label: "Direct Debit" },
          { value: "online", label: "Online" },
        ]}
      />
      <CrmNumberInput
        name="amount"
        label="Amount"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="paid_at"
        label="Paid At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="reference"
        label="Reference"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function SaleRefundsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="sale_id"
        label="Sale"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.sale_id || []}
      />
      <CrmSelectInput
        name="refunded_by_user_id"
        label="Refunded By"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.refunded_by_user_id || []}
        nullable
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "processed", label: "Processed" },
          { value: "cancelled", label: "Cancelled" },
        ]}
      />
      <CrmTextInput
        name="reason"
        label="Reason"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="total_refunded"
        label="Total Refunded"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="occurred_at"
        label="Occurred At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function SaleRefundLinesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="sale_refund_id"
        label="Sale Refund"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.sale_refund_id || []}
      />
      <CrmSelectInput
        name="sale_line_id"
        label="Sale Line"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.sale_line_id || []}
      />
      <CrmNumberInput
        name="qty"
        label="Quantity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="amount_refunded"
        label="Amount Refunded"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

const salesResources = [
  {
    key: "sales",
    label: "Sales",
    endpoint: "/sales",
    tableFields: [
      {
        name: "user_id",
        label: "Customer",
        valueGetter: (row) => row?.customer_name || row?.customer?.name || row?.customer?.email || row?.user_id,
      },
      {
        name: "sold_by_user_id",
        label: "Sold By",
        valueGetter: (row) => row?.sold_by_name || row?.sold_by_user?.name || row?.sold_by_user?.email || row?.sold_by_user_id,
      },
      { name: "channel", label: "Channel" },
      { name: "status", label: "Status" },
      { name: "subtotal", label: "Subtotal" },
      { name: "discount_total", label: "Discount Total" },
      { name: "tax_total", label: "Tax Total" },
      { name: "total", label: "Total" },
      { name: "currency", label: "Currency" },
      { name: "occurred_at", label: "Occurred At" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      { key: "sale-lines", label: "Sale Lines", endpoint: "/sale-lines", foreignKey: "sale_id" },
      { key: "sale-payments", label: "Sale Payments", endpoint: "/sale-payments", foreignKey: "sale_id" },
      { key: "sale-refunds", label: "Sale Refunds", endpoint: "/sale-refunds", foreignKey: "sale_id" },
    ],
    selectSources: [
      selectSource("user_id", "/users", ["name", "email"]),
      selectSource("sold_by_user_id", "/users", ["name", "email"]),
    ],
    renderForm: SalesForm,
    toPayload: (formData) => ({
      user_id: asNullable(formData.user_id),
      sold_by_user_id: asNullable(formData.sold_by_user_id),
      channel: asText(formData.channel),
      status: asText(formData.status),
      subtotal: asNumber(formData.subtotal),
      discount_total: asNumber(formData.discount_total),
      tax_total: asNumber(formData.tax_total),
      total: asNumber(formData.total),
      currency: asText(formData.currency),
      occurred_at: asDateTime(formData.occurred_at),
    }),
  },
  {
    key: "sale-lines",
    label: "Sale Lines",
    endpoint: "/sale-lines",
    tableFields: [
      {
        name: "sale_id",
        label: "Sale",
        valueGetter: (row) => row?.sale_label || row?.sale?.status || row?.sale_id,
      },
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "qty", label: "Qty" },
      { name: "unit_price", label: "Unit Price" },
      { name: "line_subtotal", label: "Line Subtotal" },
      {
        name: "vat_rate_id",
        label: "VAT Rate",
        valueGetter: (row) => row?.vat_rate_label || row?.vat_rate?.code || row?.vat_rate_id,
      },
      { name: "vat_rate_snapshot", label: "VAT Snapshot" },
      { name: "vat_amount", label: "VAT Amount" },
      { name: "product_name_snapshot", label: "Product Snapshot" },
      { name: "cost_snapshot", label: "Cost Snapshot" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      { key: "sale-refund-lines", label: "Refund Lines", endpoint: "/sale-refund-lines", foreignKey: "sale_line_id" },
    ],
    selectSources: [
      selectSource("sale_id", "/sales", ["uuid", "occurred_at", "status"]),
      selectSource("product_id", "/products", ["name", "code", "uuid"]),
      selectSource("vat_rate_id", "/vat-rates", ["code", "name"]),
    ],
    renderForm: SaleLinesForm,
    toPayload: (formData) => ({
      sale_id: asNullable(formData.sale_id),
      product_id: asNullable(formData.product_id),
      qty: asNumber(formData.qty),
      unit_price: asNumber(formData.unit_price),
      line_subtotal: asNumber(formData.line_subtotal),
      vat_rate_id: asNullable(formData.vat_rate_id),
      vat_rate_snapshot: asNullableNumber(formData.vat_rate_snapshot),
      vat_amount: asNumber(formData.vat_amount),
      product_name_snapshot: asText(formData.product_name_snapshot),
      cost_snapshot: asNullableNumber(formData.cost_snapshot),
    }),
  },
  {
    key: "sale-payments",
    label: "Sale Payments",
    endpoint: "/sale-payments",
    tableFields: [
      {
        name: "sale_id",
        label: "Sale",
        valueGetter: (row) => row?.sale_label || row?.sale?.status || row?.sale_id,
      },
      { name: "method", label: "Method" },
      { name: "amount", label: "Amount" },
      { name: "paid_at", label: "Paid At" },
      { name: "reference", label: "Reference" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("sale_id", "/sales", ["uuid", "occurred_at", "status"]),
    ],
    renderForm: SalePaymentsForm,
    toPayload: (formData) => ({
      sale_id: asNullable(formData.sale_id),
      method: asText(formData.method),
      amount: asNumber(formData.amount),
      paid_at: asDateTime(formData.paid_at),
      reference: asNullableText(formData.reference),
    }),
  },
  {
    key: "sale-refunds",
    label: "Sale Refunds",
    endpoint: "/sale-refunds",
    tableFields: [
      {
        name: "sale_id",
        label: "Sale",
        valueGetter: (row) => row?.sale_label || row?.sale?.status || row?.sale_id,
      },
      {
        name: "refunded_by_user_id",
        label: "Refunded By",
        valueGetter: (row) =>
          row?.refunded_by_name
          || row?.refunded_by_user?.name
          || row?.refunded_by_user?.email
          || row?.refunded_by_user_id,
      },
      { name: "status", label: "Status" },
      { name: "reason", label: "Reason" },
      { name: "total_refunded", label: "Total Refunded" },
      { name: "occurred_at", label: "Occurred At" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      { key: "sale-refund-lines", label: "Refund Lines", endpoint: "/sale-refund-lines", foreignKey: "sale_refund_id" },
    ],
    selectSources: [
      selectSource("sale_id", "/sales", ["uuid", "occurred_at", "status"]),
      selectSource("refunded_by_user_id", "/users", ["name", "email"]),
    ],
    renderForm: SaleRefundsForm,
    toPayload: (formData) => ({
      sale_id: asNullable(formData.sale_id),
      refunded_by_user_id: asNullable(formData.refunded_by_user_id),
      status: asText(formData.status),
      reason: asNullableText(formData.reason),
      total_refunded: asNumber(formData.total_refunded),
      occurred_at: asDateTime(formData.occurred_at),
    }),
  },
  {
    key: "sale-refund-lines",
    label: "Sale Refund Lines",
    endpoint: "/sale-refund-lines",
    tableFields: [
      {
        name: "sale_refund_id",
        label: "Sale Refund",
        valueGetter: (row) => row?.sale_refund_label || row?.sale_refund?.status || row?.sale_refund_id,
      },
      {
        name: "sale_line_id",
        label: "Sale Line",
        valueGetter: (row) => row?.sale_line_label || row?.sale_line?.product_name_snapshot || row?.sale_line_id,
      },
      { name: "qty", label: "Qty" },
      { name: "amount_refunded", label: "Amount Refunded" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("sale_refund_id", "/sale-refunds", ["uuid", "occurred_at", "status"]),
      selectSource("sale_line_id", "/sale-lines", ["uuid", "product_name_snapshot"]),
    ],
    renderForm: SaleRefundLinesForm,
    toPayload: (formData) => ({
      sale_refund_id: asNullable(formData.sale_refund_id),
      sale_line_id: asNullable(formData.sale_line_id),
      qty: asNumber(formData.qty),
      amount_refunded: asNumber(formData.amount_refunded),
    }),
  },
];

export default function SalesPage() {
  return (
    <SectionCrudPage
      title="Sales"
      subtitle="Manage sales, lines, payments and refunds."
      resources={salesResources}
    />
  );
}
